import { ActionContext } from 'vuex';
import dayjs from 'dayjs';
import { Between, Equal, IQueryCommandOptions, IWhereCommand, convertQueryCommandToQueryString } from '@llws/dynamic-router-utils';
import {
  IApiResponse,
  IExportConfigV2,
  IFilterPayloadV2,
  IMetaResponse,
} from '@/shared';
import { adminRequest } from '@/shared/request-configs/admin-request';
import { downloadCSV } from '@/shared/helpers/download-csv';
import { ICallTrackingLogStats } from '@/shared/interfaces/call-tracking-log-stats';

const requestEndpoints: any = {
  logs: `/call-tracking-api/view_call_tracking_call_logs`,
  activeNumbers: `/call-tracking-api/view_call_tracking_active_number_summary_new`,
  forwardingNumbers: `/call-tracking-api/view_call_tracking_number_forwarding`,
  unattachedNumbers: `/call-tracking-api/view_unattached_number_summary`,
};

export default {
  namespaced: true, // 'amenities/'
  state: {
    logs: undefined as IApiResponse<any[], IMetaResponse> | undefined,
    logStats: undefined as any,
    activeNumbers: undefined as IApiResponse<any[], IMetaResponse> | undefined,
    forwardingNumbers: undefined as IApiResponse<any[], IMetaResponse> | undefined,
    forwardingNumberStats: undefined as any,
    unattachedNumbers: undefined as IApiResponse<any[], IMetaResponse> | undefined,
  },
  getters: {
    logs(state: any): any[] {
      return [ ...state.logs?.data || [] ];
    },

    activeNumbers(state: any): any[] {
      return [ ...state.activeNumbers?.data || [] ];
    },

    forwardingNumbers(state: any): any[] {
      return [ ...state.forwardingNumbers?.data || [] ];
    },

    unattachedNumbers(state: any): any[] {
      return [ ...state.unattachedNumbers?.data || [] ];
    },

    logsMeta(state: any): any[] {
      return state.logs?.meta;
    },

    activeNumbersMeta(state: any): any[] {
      return state.activeNumbers?.meta;
    },

    forwardingNumbersMeta(state: any): any[] {
      return state.forwardingNumbers?.meta;
    },

    unattachedNumbersMeta(state: any): any[] {
      return state.unattachedNumbers?.meta;
    },

    logStats(state: any): any {
      return state.logStats;
    },

    forwardingNumberStats(state: any): any {
      return state.forwardingNumberStats;
    },
  },

  mutations: {
    set: (state: any, payload: { response: IApiResponse<any[], IMetaResponse>, type: string }) => {
      state[payload.type] = payload.response;
    },

    setLogStats: (state: any, payload: ICallTrackingLogStats) => {
      state.logStats = payload;
    },

    setForwardingNumberStats: (state: any, payload: any) => {
      state.forwardingNumberStats = payload;
    },
  },

  actions: {
    get: async (ctx: ActionContext<any, any>, payload: IFilterPayloadV2) => {
      const url = requestEndpoints[payload.additional?.type]
        + (payload.filters ? convertQueryCommandToQueryString(payload.filters!) : '');
      const response = await adminRequest.get(url);
      ctx.commit('set', { response, type: payload.additional?.type });
    },

    getLogStats: async (ctx: ActionContext<any, any>, payload: IFilterPayloadV2) => {
      let url = requestEndpoints[payload.additional?.type];

      const filters: IQueryCommandOptions = {};
      const timeRange: IWhereCommand | undefined = payload.filters!.where!.find(
        (w) => (w as IWhereCommand).field === 'timestamp',
      ) as IWhereCommand | undefined;

      const min = dayjs.unix(parseInt(timeRange!.value.split('|')[0], 10)).format('YYYY-MM-DD');
      const max = dayjs.unix(parseInt(timeRange!.value.split('|')[1], 10)).format('YYYY-MM-DD');

      filters.limit = 1;
      filters.select = ['id', 'countPhoneCalls', 'countMessages', 'avgDuration'];
      filters.aggregations = [
        {
          table: 'call_tracking_log_calls',
          propertyAlias: 'countPhoneCalls',
          whereCommands: [
            ...[ Equal('clientId', ctx.rootGetters['users/currentClient'].id)],
            ...[ Between('timestamp', min, max) ],
          ],
          column: 'id',
          aggregate: 'COUNT_DISTINCT',
        },
        {
          table: 'call_tracking_log_messages',
          propertyAlias: 'countMessages',
          whereCommands: [
            ...[ Equal('clientId', ctx.rootGetters['users/currentClient'].id)],
            ...[ Between('timestamp', min, max) ],
          ],
          column: 'id',
          aggregate: 'COUNT_DISTINCT',
        },
        {
          table: 'call_tracking_log_calls',
          propertyAlias: 'avgDuration',
          whereCommands: [
            ...[ Equal('clientId', ctx.rootGetters['users/currentClient'].id)],
            ...[ Between('timestamp', min, max) ],
          ],
          column: 'dialDuration',
          aggregate: 'AVG',
        },
      ];

      url += convertQueryCommandToQueryString(filters);
      const response = await adminRequest.get(url);
      ctx.commit('setLogStats', response.data[0]);
    },

    getForwardingNumberStats: async (ctx: ActionContext<any, any>, payload: IFilterPayloadV2) => {
      let url = requestEndpoints[payload.additional?.type];

      const filters: IQueryCommandOptions = {};

      filters.limit = 1;
      filters.select = ['countPhoneNumbers', 'countLocalNumbers', 'countTollFreeNumbers'];
      filters.aggregations = [
        {
          table: 'typeorm_call_tracking_number_forwarding_view',
          propertyAlias: 'countPhoneNumbers',
          whereCommands: [
            ...[ Equal('clientId', ctx.rootGetters['users/currentClient'].id)],
          ],
          column: 'callTrackingNumberPhoneNumber',
          aggregate: 'COUNT_DISTINCT',
        },
        {
          table: 'typeorm_call_tracking_number_forwarding_view',
          propertyAlias: 'countLocalNumbers',
          whereCommands: [
            ...[ Equal('clientId', ctx.rootGetters['users/currentClient'].id)],
            ...[ Equal('callTrackingNumberIsTollFree', '0') ],
          ],
          column: 'callTrackingNumberPhoneNumber',
          aggregate: 'COUNT',
        },
        {
          table: 'typeorm_call_tracking_number_forwarding_view',
          propertyAlias: 'countTollFreeNumbers',
          whereCommands: [
            ...[ Equal('clientId', ctx.rootGetters['users/currentClient'].id)],
            ...[ Equal('callTrackingNumberIsTollFree', '1') ],
          ],
          column: 'callTrackingNumberPhoneNumber',
          aggregate: 'COUNT',
        },
      ];

      url += convertQueryCommandToQueryString(filters);
      const response = await adminRequest.get(url);
      ctx.commit('setForwardingNumberStats', response.data[0]);
    },

    getLogsCustom: async (
      ctx: ActionContext<any, any>,
      payload: {
        timestampUnixMin?: number | string,
        timestampUnixMax?: number | string,
        logType?: string,
        status?: string,
        searchValue?: string,
        orderBy?: string,
        orderDir?: string,
        page?: string,
        limit?: string,
        exportConfig?: IExportConfigV2,
      },
    ) => {
      const url = `/call-tracking-api/clients/${ctx.rootGetters['users/currentClient'].id}/logs`;
      const query = Array
        .from(Object.keys(payload), (k) => ((payload as any)[k] ? `${k}=${(payload as any)[k]}` : ''))
        .filter((v) => v && v !== '')
        .join('&');

      if (payload.exportConfig) {
        const response = await adminRequest.post(`${url}?${query}&csvExport=true`, { fields: payload.exportConfig.fields });
        const range = `${dayjs.unix(payload.timestampUnixMin as number).format('YYYY-MM-DD')}`
          + `_to_${dayjs.unix(payload.timestampUnixMax as number).format('YYYY-MM-DD')}`;
        downloadCSV(typeof response === 'string' ? response : '', `call_logs_${range}.csv`);
      } else {
        const response = await adminRequest.get<unknown, IMetaResponse & { stats: ICallTrackingLogStats }>(`${url}?${query}`);
        ctx.commit('set', { response, type: 'logs' });
        ctx.commit('setLogStats', response?.meta?.stats);
      }
    },
  },
};
